import { SelectionOutline } from '@components/Viewer3D/Item/SelectionOutline'
import { shouldSuppressClickAfterDrag } from '@components/Viewer3D/pointerSelectionGuard'
import { SceneObjectRenderer } from '@components/ViewerManager/SceneObjectRendererENC'
import { GIZMO_SCALE_3D, ITEM_SCENE_SCALE, MODE_3D_VIEW } from '@constants'
import { useSharedCubeCamera } from '@context/CubeCameraContext'
import { useViewerContext } from '@context/viewers'
import { useGLBInstance } from '@hooks/useGLBInstance'
import { useItemScale } from '@hooks/useItemScale'
import { useMeshFloorDrag3D } from '@hooks/useMeshFloorDrag3D'
import { PivotControls, Text, useCursor } from '@react-three/drei'
import { ThreeEvent, useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { AnyID, Item, LinkedItem } from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { getInitialAngleDegrees } from '@utils/getInitialAngleDegrees'
import { pauseHistoryRecording, resumeHistoryRecording } from '@utils/historyRecording'
import { MODULAR_IKEA_DRAG_ENABLED } from '@utils/modularSnap'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import {
  findPenetratingNeighbor,
  resolveFloorDragPosition,
} from '@utils/modularSnap/collisionGuard'
import { collideModularLinkedItems } from '@utils/modularSnap/collideModular'
import {
  describeMatrix,
  describeOBB,
  isPouffDebugEnabled,
  pouffDebugNextFrame,
  pouffLog,
} from '@utils/modularSnap/pouffDragDebug'
import { buildItemOBBFromMatrix } from '@utils/sceneObjects/items/itemObbUtils'
import { isKubeCushion, isKubeModule, isPouff } from '@utils/sceneObjects/items/rinconeroUtils'
import {
  collectCushionCollisionCandidates,
  depenetrateCushionPose,
  findCushionVolumePenetration,
  hydrateKubeMeshLocalMins,
  resolveCushionFreeDragPosition,
} from '@utils/modularSnap/kubeCushion'
import { shouldAxesActivate } from '@utils/sceneObjects/items/shouldAxesActivate'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Box3, Group, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { LinkedItemSon3D } from './LinkedItemSon3D'
type Props = {
  id: Item['id']
  catalogID: Item['catalogID']
  zones: Item['zones']
  morphs: Item['morphs']
  textures: Item['textures']
  matrix: Item['matrix']
  glbSize: Item['glbSize']
}
export const LinkedItem3D = React.memo(function LinkedItem3D({
  id,
  catalogID,
  zones,
  morphs,
  textures,
  matrix,
  glbSize,
}: Props) {
  const select = useAppStore.use.select()
  const editItem = useAppStore.use.editItem()
  const isSelected = useAppStore((store) => store.layers[store.currentLayer].selected.has(id))
  const controlsRef = useRef(undefined)
  const matrixRootRef = useRef<Group>(null)
  const prefs = useAppStore.use.prefs()
  const setCurrentObjectID = useAppStore.use.setCurrentObjectID()
  const mode = useAppStore.use.mode()
  const isAlzados = mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS'

  const item = useAppStore((s) => s.items.get(id)) as LinkedItem
  const morphsFromStore = item?.morphs ?? morphs
  const isBlocked = item?.isBlocked
  const isModalOpen = useAppStore.use.isModalOpen()
  const itemRef = useRef(null)
  const visible = item?.visible
  const controls = useThree((state) => state.controls)
  const setMode = useAppStore.use.setMode()
  const childs = item?.childs as LinkedItem['childs']
  const [hovered, setHovered] = useState(false)
  const isCushion = isKubeCushion(catalogID)
  const ikeaMeshDrag = MODULAR_IKEA_DRAG_ENABLED && !isAlzados
  const glb = useGLBInstance(String(catalogID), id)
  /**
   * Cojín K1C: meshLocalMin suele ser {0, ~0.35, ~-0.28}.
   * Si solo asentamos Y, el mesh queda desplazado en Z respecto a la OBB →
   * al rotar/arrastrar “entra” en el respaldo sin detectar colisión.
   */
  const meshLocalMin = useMemo(() => {
    const stored = (item as LinkedItem | undefined)?.meshLocalMin
    if (
      stored &&
      Number.isFinite(stored.x) &&
      Number.isFinite(stored.y) &&
      Number.isFinite(stored.z)
    ) {
      return stored
    }
    const yOnly = Number((item as LinkedItem | undefined)?.meshLocalYMin)
    if (Number.isFinite(yOnly) && (yOnly !== 0 || isCushion)) {
      return { x: 0, y: yOnly || 0, z: 0 }
    }
    if (!isCushion || !glb) return null
    const root = (glb as any)?.scene ?? glb
    if (!root) return null
    const box = new Box3()
    let hasVis = false
    root.traverse((obj: any) => {
      if (!obj?.isMesh) return
      let n: any = obj
      let vis = true
      while (n) {
        if (n.visible === false) {
          vis = false
          break
        }
        n = n.parent
      }
      if (!vis) return
      box.expandByObject(obj)
      hasVis = true
    })
    if (!hasVis || box.isEmpty()) return null
    return { x: box.min.x, y: box.min.y, z: box.min.z }
  }, [isCushion, item, glb])
  useCursor(hovered)

  useEffect(() => {
    if (!ikeaMeshDrag || !hovered || !isSelected || isBlocked) return
    document.body.style.cursor = 'grab'
    return () => {
      document.body.style.cursor = ''
    }
  }, [ikeaMeshDrag, hovered, isSelected, isBlocked])
  const currentObjectID = useAppStore.use.currentObjectID()
  const renderMorphs = morphsFromStore
  const selectedItems = useAppStore(
    (store) => store.layers[store.currentLayer].selected,
  ) as Set<AnyID>
  const setDeltas = useAppStore.use.setDeltas()
  const unselectAll = useAppStore.use.unselectAll()
  const updateSelectedObjectsTraslation = useAppStore.use.updateSelectedObjectsTraslation()
  const updateSelectedObjectsPosition = useAppStore.use.updateSelectedObjectsPosition()
  useViewerContext()
  const itemBB = useMemo(() => {
    return new OBB()
  }, [])
  const { sizeRef, scaleVector } = useItemScale(id, morphsFromStore, glbSize)
  const setOBBs = useAppStore.use.setOBBScene()
  const originalModeRef = useRef<string | undefined>(undefined)

  function handleClick(e: ThreeEvent<MouseEvent>) {
    if (isModalOpen) return
    if (shouldSuppressClickAfterDrag()) return
    unselectAll()
    e.stopPropagation()
    select(id, e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)
    setCurrentObjectID(id)
    if (isBlocked) return
  }
  function handleDragStart() {
    if (isModalOpen || isBlocked) return
    setOBBs() // IMPORTANTE REVISAR LOS DEMAS setOBBS (Update OBBs for collision detection and snapping)
    // Posición de partida = última posición válida conocida (el item no solapa al iniciar).
    lastValidMatrixRef.current = matrix ? matrix.clone() : null

    // Si el cojín ya nació/quedó dentro, sacarlo antes de arrastrar.
    if (isKubeCushion(catalogID) && matrix) {
      const liveItems = useAppStore.getState().items as Map<string, LinkedItem>
      hydrateKubeMeshLocalMins(liveItems, useAppStore.getState().sceneGLB as Map<any, any>)
      const candidates = collectCushionCollisionCandidates(liveItems, id)
      const liveItem = liveItems.get(String(id)) as LinkedItem | undefined
      if (liveItem) {
        const hit = findCushionVolumePenetration(
          id,
          matrix as Matrix4,
          liveItem,
          liveItems,
          candidates,
        )
        if (hit) {
          const pos = new Vector3()
          const quat = new Quaternion()
          ;(matrix as Matrix4).decompose(pos, quat, new Vector3())
          const freed = depenetrateCushionPose(id, pos, quat, liveItem, liveItems, candidates, hit)
          const safe = new Matrix4().compose(freed, quat, new Vector3(1, 1, 1))
          editItem(id, { x: freed.x, y: freed.y, z: freed.z, matrix: safe.clone() })
          lastValidMatrixRef.current = safe.clone()
        }
      }
    }

    setCurrentObjectID(id)
    // Limpiar snappedSides del item arrastrado: al moverlo deja de estar anclado a ningún lado
    editItem(id, { snappedSides: {} } as any)
    // Limpiar referencias a este item en los snappedSides de otros items
    const allItems = useAppStore.getState().items
    allItems.forEach((otherItem, otherId) => {
      if (otherId === id) return
      const ss = (otherItem as any).snappedSides as
        Partial<Record<'left' | 'right' | 'top' | 'bottom', string>> | undefined
      if (!ss) return
      const touchesDragged = ss.left === id || ss.right === id || ss.top === id || ss.bottom === id
      if (!touchesDragged) return
      editItem(
        otherId as any,
        {
          snappedSides: {
            left: ss.left === id ? undefined : ss.left,
            right: ss.right === id ? undefined : ss.right,
            top: ss.top === id ? undefined : ss.top,
            bottom: ss.bottom === id ? undefined : ss.bottom,
          },
        } as any,
      )
    })
    if (selectedItems.size > 1) {
      const currentItems = useAppStore.getState().items
      setDeltas(calculateDeltasBetweenSelectedItems(selectedItems, currentItems))
      if (mode !== 'MODE_FORCE_ALZADOS' && mode !== 'MODE_ALZADOS') {
        enterModularDragMode()
      }
    }
  }

  function handleMissed() {
    if (isModalOpen) return
    if (mode === 'MODE_DRAWING_ITEM') {
      return setMode('MODE_3D_VIEW')
    }
  }
  const initialAngleDegrees = getInitialAngleDegrees(item)
  const gizmoColorX = isSelected && currentObjectID === id ? prefs['C/GIZMOX'] : prefs['C/GIZMOZ']
  const gizmoColorZ = isSelected && currentObjectID === id ? prefs['C/GIZMOY'] : prefs['C/GIZMOZ']
  const collisionableItems = useAppStore.use.collisionableItems()
  const collideItems = useAppStore.use.collideItems()
  const collidePouffItems = useAppStore.use.collidePouffItems()
  const addContador = useAppStore.use.addContadorModificaciones()
  const hasCollided = useRef(false)
  // Última posición no-solapante confirmada durante el arrastre; se usa para restaurar
  // el item cuando el destino propuesto penetraría otra unidad (ver handleDrag).
  const lastValidMatrixRef = useRef<Matrix4 | null>(null)

  const enterModularDragMode = () => {
    const store = useAppStore.getState()
    if (store.mode === 'MODE_FORCE_ALZADOS' || store.mode === 'MODE_ALZADOS') return
    if (store.mode !== 'MODE_DRAGGING_ITEM_3D') {
      originalModeRef.current = store.mode
    } else if (!originalModeRef.current) {
      originalModeRef.current = MODE_3D_VIEW
    }
    setMode('MODE_DRAGGING_ITEM_3D')
  }

  const finishModularDragMode = () => {
    useModularUiStore.getState().setItemDragActive(false)
    const store = useAppStore.getState()
    if (store.mode === 'MODE_DRAGGING_ITEM_3D') {
      setMode(originalModeRef.current ?? MODE_3D_VIEW)
    }
    originalModeRef.current = undefined
  }

  function handleDrag<T extends Matrix4>(l: Matrix4, dl: T, w: T) {
    const activeObjectID = useAppStore.getState().currentObjectID
    if (isModalOpen || isBlocked || activeObjectID !== id) return
    if (!itemRef.current) return
    const items = useAppStore.getState().items
    if (controlsRef.current === 'Rotator') {
      setDeltas(calculateDeltasBetweenSelectedItems(selectedItems, items))
      editItem(id, { matrix: w.clone() })
      // console.log(initialAngleDegrees, w.clone())
      if (selectedItems.size > 1) {
        updateSelectedObjectsTraslation(id, l, initialAngleDegrees)
      }
    } else {
      const itemrot = toQuaternion()
      const itemScale = toVector3()
      matrix?.decompose(toVector3(), itemrot, itemScale)

      const itemPosition = toVector3()
      l.decompose(itemPosition, toQuaternion(), toVector3())

      const proposedMatrix = new Matrix4()
        .makeRotationFromQuaternion(itemrot)
        .setPosition(itemPosition.x, itemPosition.y, itemPosition.z)

      // Cojines: libre en X/Y/Z, pero nunca penetrar otras unidades (contacto de cara OK).
      if (isKubeCushion(item.catalogID)) {
        const freeY = Math.max(0, itemPosition.y)
        const cushionProposed = proposedMatrix
          .clone()
          .setPosition(itemPosition.x, freeY, itemPosition.z)

        const lastValid = lastValidMatrixRef.current ?? (matrix as Matrix4)
        const liveItems = useAppStore.getState().items as Map<string, LinkedItem>
        hydrateKubeMeshLocalMins(liveItems, useAppStore.getState().sceneGLB as Map<any, any>)
        const candidates = collectCushionCollisionCandidates(liveItems, id)
        const liveItem = (liveItems.get(String(id)) as LinkedItem | undefined) ?? item

        const resolved = resolveCushionFreeDragPosition(
          id,
          lastValid,
          cushionProposed,
          liveItem,
          liveItems,
          candidates,
        )
        // Rechazo duro: no reescribir la misma pose (evita tropezones).
        if (!resolved) return

        const resolvedPos = new Vector3()
        resolved.decompose(resolvedPos, new Quaternion(), new Vector3())
        editItem(id, {
          x: resolvedPos.x,
          y: resolvedPos.y,
          z: resolvedPos.z,
          matrix: resolved.clone(),
        })
        updateSelectedObjectsPosition(id, resolved)
        lastValidMatrixRef.current = resolved.clone()
        return
      }

      buildItemOBBFromMatrix(proposedMatrix, item.width, item.height, item.thickness, itemBB)
      itemBB.halfSize.multiply(toVector3([1.5, 1.5, 1.5]))
      let hasCollision = false
      let snapAnchorId: AnyID | null = null

      const dbg = isPouffDebugEnabled() && (isPouff(item.catalogID) || isKubeModule(item.catalogID))
      if (dbg) {
        pouffDebugNextFrame()
        const storeBefore = useAppStore.getState().items.get(String(id)) as LinkedItem | undefined
        const renderedWorld = itemRef.current
          ? (itemRef.current as unknown as Group).matrixWorld
          : null
        pouffLog('DRAG/IN', {
          id: String(id),
          uuid: (item as unknown as { uuid?: string }).uuid,
          catalog: item.catalogID,
          dims: { w: item.width, h: item.height, t: item.thickness },
          inCollisionable: collisionableItems.has(id),
          proposedMatrix_L: describeMatrix(l),
          storeMatrix_before: describeMatrix(storeBefore?.matrix as Matrix4 | undefined),
          renderedMatrixWorld_three: describeMatrix(renderedWorld as Matrix4 | null),
          lastValidMatrix: describeMatrix(lastValidMatrixRef.current),
          itemBB_broadphase_x1_5: describeOBB(itemBB),
        })
      }

      if (collisionableItems.has(id)) {
        editItem(id, {
          x: itemPosition.x,
          y: itemPosition.y,
          z: itemPosition.z,
          matrix: proposedMatrix.clone(),
        })

        const liveItems = useAppStore.getState().items
        const collisionableItemsWithDistance: Array<{
          id: AnyID
          distance2D: number
        }> = []

        collisionableItems.forEach((collisionableItem) => {
          if (collisionableItem !== id) {
            const otherItem = liveItems.get(collisionableItem)
            if (!otherItem?.matrix) return

            const otherItemPos = new Vector3()
            otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())

            const distance2D = Math.sqrt(
              Math.pow(itemPosition.x - otherItemPos.x, 2) +
                Math.pow(itemPosition.z - otherItemPos.z, 2),
            )

            collisionableItemsWithDistance.push({
              id: collisionableItem,
              distance2D,
            })
          }
        })

        collisionableItemsWithDistance.sort((a, b) => a.distance2D - b.distance2D)

        for (const { id: collisionableItem } of collisionableItemsWithDistance) {
          if (hasCollision) break

          const otherItem = liveItems.get(collisionableItem) as LinkedItem | undefined
          if (!otherItem?.matrix) continue

          const neighborOBB = buildItemOBBFromMatrix(
            otherItem.matrix as Matrix4,
            otherItem.width,
            otherItem.height,
            otherItem.thickness,
          )
          const broadphaseHit = itemBB.intersectsOBB(neighborOBB)

          if (dbg) {
            const otherPos = new Vector3()
            otherItem.matrix.decompose(otherPos, new Quaternion(), new Vector3())
            pouffLog('DRAG/BROADPHASE', {
              dragId: String(id),
              anchorId: String(collisionableItem),
              anchorCatalog: otherItem.catalogID,
              broadphaseHit_x1_5: broadphaseHit,
              distanceCenters2D:
                Math.round(
                  itemPosition.distanceTo(new Vector3(otherPos.x, itemPosition.y, otherPos.z)) *
                    100,
                ) / 100,
              anchorOBB_exact: describeOBB(neighborOBB),
            })
          }

          if (broadphaseHit) {
            const snapAttempt = collideModularLinkedItems(
              id,
              collisionableItem,
              (oid) => liveItems.get(oid)?.catalogID,
              collideItems,
              collidePouffItems,
            )
            if (dbg) {
              pouffLog('DRAG/SNAP_ATTEMPT', {
                dragId: String(id),
                anchorId: String(collisionableItem),
                snapped: snapAttempt?.snapped ?? false,
                side: snapAttempt?.side ?? null,
                storeMatrix_afterSnap: describeMatrix(
                  useAppStore.getState().items.get(String(id))?.matrix as Matrix4 | undefined,
                ),
              })
            }
            if (snapAttempt?.snapped) {
              hasCollision = true
              hasCollided.current = true
              snapAnchorId = collisionableItem
              break
            }
          }
        }
      }

      const storeAfter = useAppStore.getState()
      const resultMatrix = (
        hasCollision ? storeAfter.items.get(String(id))?.matrix : proposedMatrix
      ) as Matrix4 | undefined
      if (!resultMatrix) return

      if (hasCollision) {
        const penetratedBy = collisionableItems.has(id)
          ? findPenetratingNeighbor(
              id,
              resultMatrix,
              item,
              storeAfter.items,
              collisionableItems,
              snapAnchorId ? [snapAnchorId] : [],
            )
          : null

        if (dbg) {
          pouffLog('DRAG/STRICT_GUARD', {
            dragId: String(id),
            hasCollision: true,
            snapAnchorId: snapAnchorId ? String(snapAnchorId) : null,
            penetratedBy: penetratedBy ? String(penetratedBy) : null,
            resultMatrix: describeMatrix(resultMatrix),
          })
        }

        if (penetratedBy) {
          const safe = lastValidMatrixRef.current
          if (safe) {
            const safePos = new Vector3()
            safe.decompose(safePos, new Quaternion(), new Vector3())
            editItem(id, {
              x: safePos.x,
              y: safePos.y,
              z: safePos.z,
              matrix: safe.clone(),
            })
          }
          return
        }

        lastValidMatrixRef.current = resultMatrix.clone()
      } else {
        const lastValid = lastValidMatrixRef.current
        const slideBase = lastValid ?? (matrix as Matrix4)
        const resolved =
          collisionableItems.has(id) && lastValid
            ? resolveFloorDragPosition(
                id,
                slideBase,
                proposedMatrix,
                item,
                storeAfter.items,
                collisionableItems,
              )
            : proposedMatrix.clone()

        if (!resolved) {
          if (lastValid) {
            const safePos = new Vector3()
            lastValid.decompose(safePos, new Quaternion(), new Vector3())
            editItem(id, {
              x: safePos.x,
              y: safePos.y,
              z: safePos.z,
              matrix: lastValid.clone(),
            })
          }
          return
        }

        const resolvedPos = new Vector3()
        resolved.decompose(resolvedPos, new Quaternion(), new Vector3())

        let restrictedY = resolvedPos.y
        if (mode === 'MODE_3D_VIEW') {
          restrictedY = Math.max(0, restrictedY)
        }

        const restrictedMatrix = resolved
          .clone()
          .setPosition(resolvedPos.x, restrictedY, resolvedPos.z)

        editItem(id, {
          x: resolvedPos.x,
          z: resolvedPos.z,
          y: restrictedY,
          matrix: restrictedMatrix,
        })

        updateSelectedObjectsPosition(id, restrictedMatrix)
        lastValidMatrixRef.current = restrictedMatrix.clone()

        if (dbg) {
          pouffLog('DRAG/APPLY_FREE', {
            dragId: String(id),
            appliedMatrix: describeMatrix(restrictedMatrix),
            slidFromLastValid: !!lastValid,
            storeMatrix_after: describeMatrix(
              useAppStore.getState().items.get(String(id))?.matrix as Matrix4 | undefined,
            ),
          })
        }
      }
    }
  }
  // const items = useAppStore((s) => s.items)
  const textoManoIZQ = useAppStore((s) => s.prefs.MANOIZQUIERDA)
  const textoManoDCH = useAppStore((s) => s.prefs.MANODERECHA)
  const colorTextoMano = useAppStore((s) => s.prefs.COLORTEXTOBISAGRA)
  const verBisagras = useAppStore((s) => s.prefs.VERBISAGRAALZADO)
  const showText = verBisagras && mode === 'MODE_FORCE_ALZADOS' && item?.bisagra
  const isGolaItem = typeof catalogID === 'string' && catalogID.toLowerCase().includes('g')

  const getGolaText = () => {
    if (!isGolaItem || item?.hinge === undefined) return ''
    return item.hinge === false ? textoManoDCH : textoManoIZQ
  }

  const textXPosition = (0.3 * item?.width) / 600
  const textYPosition = 0.84

  // Usar el renderTarget del contexto CubeCamera
  const { renderTarget } = useSharedCubeCamera() || {}

  const { meshPointerHandlers, suppressClick } = useMeshFloorDrag3D({
    enabled: ikeaMeshDrag,
    id,
    matrix,
    isBlocked,
    isModalOpen,
    mode,
    currentObjectID,
    dragMode: isCushion ? 'free' : 'floor',
    matrixRootRef,
    onPointerDownSelect: (e) => {
      select(id, e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)
      setCurrentObjectID(id)
    },
    onDragStart: () => {
      controlsRef.current = undefined
      pauseHistoryRecording()
      handleDragStart()
      const store = useAppStore.getState()
      if (store.layers[store.currentLayer].selected.size <= 1) {
        enterModularDragMode()
      }
    },
    onDrag: handleDrag,
    onDragEnd: () => {
      resumeHistoryRecording()
      controlsRef.current = undefined
      finishModularDragMode()
      if (hasCollided.current && item.tipoNormalizado === 'MBJ') {
        addContador()
        hasCollided.current = false
      }
    },
  })

  useLayoutEffect(() => {
    if (!ikeaMeshDrag || !matrixRootRef.current || !matrix) return
    matrixRootRef.current.matrix.copy(matrix)
    matrixRootRef.current.matrixAutoUpdate = false
    matrixRootRef.current.updateMatrixWorld(true)
  }, [ikeaMeshDrag, matrix])

  // DIAGNÓSTICO (default-on, silenciar con window.__DRAW_DEBUG__ = false):
  // mide el tamaño REAL del mesh renderizado y lo compara con la caja de colisión
  // (width/fondo). Si el mesh es más grande, las unidades "se montan" aunque su
  // colisión esté pegada.
  useEffect(() => {
    const drawDebug =
      typeof window !== 'undefined' &&
      (window as Window & { __DRAW_DEBUG__?: boolean }).__DRAW_DEBUG__ !== false
    if (!drawDebug || !isKubeModule(catalogID)) return
    const t = setTimeout(() => {
      const node = itemRef.current as unknown as Group | null
      if (!node) return
      const box = new Box3().setFromObject(node)
      const size = box.getSize(new Vector3())
      // Tamaño SOLO de meshes visibles (excluye outline de selección, helpers y
      // cajas invisibles). Es lo que el usuario ve realmente como espuma.
      const visBox = new Box3()
      let hasVis = false
      node.traverse((obj: any) => {
        if (!obj?.isMesh) return
        let n: any = obj
        let vis = true
        while (n) {
          if (n.visible === false) {
            vis = false
            break
          }
          n = n.parent
        }
        // Excluir el contorno de selección (suele llamarse outline/edges).
        const nm = String(obj.name || '').toLowerCase()
        if (nm.includes('outline') || nm.includes('edge') || nm.includes('selection')) return
        if (!vis) return
        visBox.expandByObject(obj)
        hasVis = true
      })
      const visSize = hasVis ? visBox.getSize(new Vector3()) : null
      const r1 = (n: number) => Math.round(n * 10) / 10
      // console.log('[DRAW][MESH] tamaño renderizado vs colisión', {
      //   id: String(id),
      //   catalog: catalogID,
      //   meshRenderWorld_todo: { x: r1(size.x), y: r1(size.y), z: r1(size.z) },
      //   meshVisibleSolo: visSize
      //     ? { x: r1(visSize.x), y: r1(visSize.y), z: r1(visSize.z) }
      //     : null,
      //   cajaColision: { ancho: item?.width, alto: item?.height, fondo: item?.thickness },
      //   ratio: {
      //     x: item?.width ? r1(size.x / item.width) : null,
      //     z: item?.thickness ? r1(size.z / item.thickness) : null,
      //   },
      // })
    }, 500)
    return () => clearTimeout(t)
  }, [id, glb?.uuid])

  const itemContent = (
    <group
      key={item?.childrenOrder ? item.childrenOrder.join('-') : `${id}-default-key`}
      ref={itemRef}
      name={id}
      scale={ITEM_SCENE_SCALE}
      onPointerMissed={handleMissed}
      onPointerOver={(e) => (e.stopPropagation(), setHovered(true))}
      onPointerOut={(e) => setHovered(false)}
      onClick={(e) => {
        if (suppressClick()) return
        handleClick(e)
      }}
      onDoubleClick={() => {
        controls &&
          controls?.fitToBox(itemRef.current, true, { paddingLeft: 0.5, paddingRight: 0.5 })
      }}
      visible={visible}
    >
      <group
        ref={sizeRef}
        scale={scaleVector}
        // Asienta la espuma en XYZ al origen (OBB = [0→size] en cada eje).
        position={
          isCushion && meshLocalMin
            ? [-meshLocalMin.x, -meshLocalMin.y, -meshLocalMin.z]
            : [0, 0, 0]
        }
        {...(ikeaMeshDrag ? meshPointerHandlers : {})}
      >
        {ikeaMeshDrag && (
          <SelectionOutline
            id={id}
            width={item.width}
            height={item.height}
            thickness={item.thickness}
            parentGroupRef={sizeRef}
            forceVisible={isSelected && !isBlocked}
            meshRevision={glb?.uuid ?? 'pending'}
          />
        )}
        <SceneObjectRenderer
          key={glb?.uuid}
          id={id}
          object={glb}
          zones={zones}
          morphs={renderMorphs}
          textures={textures}
          renderTarget={renderTarget}
          paginaPdf={item.paginaPdf}
          itemHeight={item.height}
          itemWidth={item.width}
          itemThickness={item.thickness}
          getContiguousItems={useAppStore.use.getContItemsForEncimeras()}
          fatherId={id}
        />
      </group>
      {showText && (
        <Text
          position={[textXPosition, textYPosition, 0.99]}
          fontSize={0.05}
          color={colorTextoMano}
          anchorX='center'
          anchorY='middle'
        >
          {getGolaText()}
        </Text>
      )}
      {childs &&
        visible &&
        childs.size > 0 &&
        Array.from(childs).map((childId) => (
          <LinkedItemSon3D
            id={childId}
            key={childId}
            renderTarget={renderTarget}
            itemWidth={item.width}
            fatherId={id}
            matrix={matrix}
          />
        ))}
    </group>
  )

  if (ikeaMeshDrag) {
    return (
      <group
        ref={matrixRootRef}
        matrix={matrix}
        matrixAutoUpdate={false}
      >
        {itemContent}
      </group>
    )
  }

  return (
    <PivotControls
      lineWidth={3}
      ref={itemRef}
      // rotation={ITEMS_REVERT_FIXED_ROTATION.toArray()}
      autoTransform={false}
      key={`${id}-${textures}`}
      scale={GIZMO_SCALE_3D}
      visible={visible && mode !== 'MODE_DRAGGING_HOLE'}
      matrix={matrix}
      onDrag={handleDrag}
      onDragStart={(e) => {
        controlsRef.current = e.component
        pauseHistoryRecording()
        handleDragStart()
        enterModularDragMode()
      }}
      onDragEnd={() => {
        resumeHistoryRecording()
        controlsRef.current = undefined
        finishModularDragMode()
        if (hasCollided.current) {
          addContador()
          hasCollided.current = false // Resetear para el próximo drag
        }
      }}
      activeAxes={shouldAxesActivate(isSelected && !isBlocked, mode)}
      rotationLimits={[[0, 0], undefined, [0, 0]]}
      axisColors={[gizmoColorX, prefs['C/GIZMOZ'], gizmoColorZ]}
      depthTest={false}
      disableScaling
      userData={{ pivotControlsVisible: true }}
    >
      {itemContent}
    </PivotControls>
  )
})
